import React, { useState, useEffect } from 'react';
import { Crown, ShoppingBag, Users, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { SellerOnboardingDialog } from './seller/SellerOnboardingDialog';

// Set the launch date to 2 weeks from now
const LAUNCH_DATE = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export function CountdownPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isLaunched, setIsLaunched] = useState(false);
  const [showSellerOnboarding, setShowSellerOnboarding] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const difference = LAUNCH_DATE.getTime() - new Date().getTime();
      
      if (difference <= 0) {
        setIsLaunched(true);
        return;
      }

      setTimeLeft({
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60)
      });
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, []);

  if (isLaunched) {
    navigate('/marketplace/browse');
    return null;
  }

  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center p-4">
      {/* Header */}
      <div className="text-center mb-12 animate-fade-in">
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="relative">
            <div className="absolute inset-0 bg-royal-purple/20 rounded-full blur-xl animate-pulse" />
            <Crown className="w-12 h-12 text-royal-purple relative animate-float" />
          </div>
          <h1 className="text-4xl font-bold text-royal-purple">
            RoyaltySuite Marketplace
          </h1>
        </div>
        <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          Our exclusive marketplace is launching soon. Join us in creating a unique shopping experience for distinguished individuals.
        </p>
      </div>

      {/* Countdown */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-8 mb-12">
        {[
          { label: 'Days', value: timeLeft.days },
          { label: 'Hours', value: timeLeft.hours },
          { label: 'Minutes', value: timeLeft.minutes },
          { label: 'Seconds', value: timeLeft.seconds }
        ].map((item, index) => (
          <div
            key={item.label}
            className={cn(
              "flex flex-col items-center p-6 bg-white dark:bg-dark-paper rounded-xl shadow-lg hover:shadow-xl transition-shadow animate-scale-in",
              `animation-delay-${index + 1}00`
            )}
          >
            <span className="text-4xl sm:text-5xl font-bold text-royal-purple mb-2">
              {item.value.toString().padStart(2, '0')}
            </span>
            <span className="text-gray-500 dark:text-gray-400">{item.label}</span>
          </div>
        ))}
      </div>

      {/* Features */}
      <div className="grid gap-6 sm:grid-cols-3 max-w-4xl mx-auto mb-12">
        <div className="bg-white dark:bg-dark-paper p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 animate-slide-up stagger-1">
          <div className="bg-royal-purple/10 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
            <ShoppingBag className="w-6 h-6 text-royal-purple" />
          </div>
          <h3 className="text-lg font-semibold mb-2">Curated Products</h3>
          <p className="text-gray-600 dark:text-gray-400">
            Discover exclusive items from verified sellers, carefully selected for our distinguished community.
          </p>
        </div>

        <div className="bg-white dark:bg-dark-paper p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 animate-slide-up stagger-2">
          <div className="bg-royal-purple/10 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
            <Star className="w-6 h-6 text-royal-purple" />
          </div>
          <h3 className="text-lg font-semibold mb-2">Premium Quality</h3>
          <p className="text-gray-600 dark:text-gray-400">
            Every product meets our high standards of quality and authenticity, guaranteed.
          </p>
        </div>

        <div className="bg-white dark:bg-dark-paper p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 animate-slide-up stagger-3">
          <div className="bg-royal-purple/10 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
            <Users className="w-6 h-6 text-royal-purple" />
          </div>
          <h3 className="text-lg font-semibold mb-2">Exclusive Community</h3>
          <p className="text-gray-600 dark:text-gray-400">
            Join a community of distinguished buyers and sellers in a secure, verified marketplace.
          </p>
        </div>
      </div>

      {/* CTA */}
      <div className="text-center animate-fade-in stagger-4">
        <button
          onClick={() => setShowSellerOnboarding(true)}
          className="px-8 py-3 bg-royal-purple text-white rounded-lg font-medium hover:bg-royal-purple/90 transition-colors"
        >
          Become a Seller
        </button>
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
          Get ready to showcase your products to our exclusive community
        </p>
      </div>

      {/* Seller Onboarding Dialog */}
      {showSellerOnboarding && (
        <SellerOnboardingDialog onClose={() => setShowSellerOnboarding(false)} />
      )}
    </div>
  );
}